export class ApiError extends Error {
  status?: number;
  code?: string;
  /** Контур аккаунта, если сервер его сообщил (коды wrong_contour / main_site_only). */
  accountKind?: "school" | "external";
  constructor(
    message: string,
    status?: number,
    code?: string,
    accountKind?: "school" | "external"
  ) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.accountKind = accountKind;
  }
}

type ApiOptions = RequestInit & {
  json?: unknown;
};

export async function apiFetch<T = unknown>(
  path: string,
  opts: ApiOptions = {}
): Promise<T> {
  const { json, headers, body, ...rest } = opts;
  const init: RequestInit = {
    credentials: "include",
    ...rest,
    headers: { ...(headers ?? {}) },
  };

  if (json !== undefined) {
    (init.headers as Record<string, string>)["Content-Type"] = "application/json";
    init.body = JSON.stringify(json);
  } else if (body !== undefined) {
    init.body = body;
  }

  const res = await fetch(path, init);
  const text = await res.text();
  let data: Record<string, unknown> = {};
  if (text) {
    try {
      data = JSON.parse(text) as Record<string, unknown>;
    } catch {
      throw new ApiError(
        `Сервер вернул не JSON (${res.status}). Проверьте прокси /api/.`,
        res.status
      );
    }
  }

  if (!res.ok) {
    const accountKind =
      data.account_kind === "school" || data.account_kind === "external"
        ? data.account_kind
        : undefined;
    throw new ApiError(
      (typeof data.error === "string" && data.error) || res.statusText,
      res.status,
      typeof data.code === "string" ? data.code : undefined,
      accountKind
    );
  }

  return data as T;
}

export type MeResponse = {
  id: string;
  email: string;
  account_kind?: "school" | "external";
  plan?: { code: string; status: string; source?: string } | null;
  plan_limits?: {
    max_boards: number;
    owned_boards: number;
    max_folders: number;
    owned_folders: number;
    allow_share_links: boolean;
    allow_media: boolean;
    allow_checkpoints: boolean;
    can_create_board: boolean;
    build?: string;
  } | null;
  external?: { provider: string; userId: string } | null;
};

export type BoardSummary = {
  id: string;
  title: string;
  /** Короткая заметка о доске; `null` — описания нет. */
  description?: string | null;
  folder_id: string | null;
  created_at: string;
  updated_at: string;
  is_owner?: boolean;
  membership_role?: "editor" | "viewer" | null;
  access_via?: "owner" | "board_member" | "folder" | "shortcut";
  folder_access_role?: "editor" | "viewer" | null;
  is_starred?: boolean;
  /** Закреплена в левой панели кабинета. */
  is_pinned?: boolean;
  /** Человек держит доску у себя по ссылке, участником не является. */
  is_shortcut?: boolean;
};

/** Доска, добавленная по ссылке: убрать её у себя. */
export async function removeBoardShortcut(boardId: string): Promise<void> {
  await apiFetch(`/api/v1/board-shortcuts/${encodeURIComponent(boardId)}`, {
    method: "DELETE",
  });
}

/** Закрепить доску в левой панели (или открепить). */
export async function setBoardPinned(boardId: string, pinned: boolean): Promise<void> {
  await apiFetch(`/api/v1/boards/${encodeURIComponent(boardId)}/pin`, {
    method: pinned ? "PUT" : "DELETE",
  });
}

/**
 * Разбирает ссылку на доску: сам адрес и токен из «?t=».
 * Берём и полный адрес, и просто идентификатор доски — люди присылают по-разному.
 */
export function parseBoardLink(input: string): { boardId: string; token: string | null } | null {
  const text = input.trim();
  if (!text) return null;
  const uuid = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;
  const direct = uuid.exec(text);
  if (!direct) return null;
  let token: string | null = null;
  const tokenMatch = /[?&]t=([^&#\s]+)/.exec(text);
  if (tokenMatch) {
    try {
      token = decodeURIComponent(tokenMatch[1]!);
    } catch {
      token = tokenMatch[1]!;
    }
  }
  return { boardId: direct[0].toLowerCase(), token };
}

/**
 * Добавить доску себе по ссылке: заводит гостевой сеанс по токену, а сервер
 * запоминает доску за человеком — после этого она видна в «Со мной».
 */
export async function addBoardByLink(
  input: string
): Promise<{ ok: true; boardId: string } | { ok: false; error: string }> {
  const parsed = parseBoardLink(input);
  if (!parsed) return { ok: false, error: "Не похоже на ссылку доски" };
  if (!parsed.token) {
    return { ok: false, error: "В ссылке нет кода доступа (часть «?t=…»)" };
  }
  const res = await fetch(
    `/api/v1/boards/${encodeURIComponent(parsed.boardId)}/guest-session`,
    {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token: parsed.token }),
    }
  );
  const data = (await res.json().catch(() => null)) as
    | { code?: string; error?: string }
    | null;
  // Своя доска: она и так в «Моих», ярлык ей не нужен.
  if (data?.code === "session_access") {
    return { ok: true, boardId: parsed.boardId };
  }
  // Ярлык нужен ВСЕГДА, а не только когда сеанс не завёлся.
  //
  // Гостевой сеанс открывает доску, но в списке кабинета её держит именно
  // ярлык: запрос досок отбирает их по правам аккаунта или по ярлыку, а
  // cookie-сеанс не то и не другое. Раньше при политике «по ссылке» сеанс
  // создавался (201) и функция сразу отвечала «готово» — человек видел
  // «доска добавлена», а в кабинете её не было.
  const remembered = await rememberBoardByLink(parsed.boardId, parsed.token);
  if (remembered || res.status === 201) {
    return { ok: true, boardId: parsed.boardId };
  }
  return { ok: false, error: data?.error || "Ссылка не подошла" };
}

/**
 * Запомнить доску, открытую по ссылке, в списке «Со мной».
 * Прав не добавляет: доска видна, пока жива сама ссылка.
 * Молча ничего не делает, если человек не вошёл в аккаунт.
 */
export async function rememberBoardByLink(
  boardId: string,
  token: string
): Promise<boolean> {
  try {
    const res = await fetch("/api/v1/board-shortcuts", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ board_id: boardId, token }),
    });
    return res.ok;
  } catch {
    return false;
  }
}

export type FolderRow = {
  id: string;
  name: string;
  parent_id: string | null;
  sort_order: number;
};

export type SharedFolderRow = FolderRow & {
  shared_root_id: string;
  membership_role: "editor" | "viewer";
  owner_email: string;
};

export type FoldersResponse = {
  folders?: FolderRow[];
  owned: FolderRow[];
  shared: SharedFolderRow[];
};

export type BoardLoadResponse = {
  board: { id: string; title: string; description?: string | null };
  snapshot: { revision: number; scene: unknown };
  capabilities?: { edit_snapshot?: boolean; edit_info?: boolean };
  access?: string;
};

export type BoardRecording = {
  room_id: string;
  board_id: string;
  board_title: string;
  label: string | null;
  recorded_at: string;
  size_bytes: number | null;
  download_path: string;
  source?: "lesson" | "manual";
};

export type CabinetNotification = {
  id: string;
  kind: "comment";
  board_id: string;
  board_title: string;
  actor_email: string | null;
  created_at: string;
  object_id: string | null;
  preview: string | null;
  open_path: string;
};
